import { CompanyInactivePeriodDto } from '../models/api/companyInactivePeriodDto';

export type CompanyYearStatus = 'before_start' | 'inactive' | 'active';

export interface CompanyYearClassification {
  status: CompanyYearStatus;
  /** First year the company was active. `null` when undeterminable (no periods, or open-ended-from-start). */
  firstActiveYear: number | null;
}

/**
 * Classifies a year against a company's inactive periods.
 *
 * - `'before_start'` — the year falls before the company's first active year
 *   (i.e., there is a leading inactive period that ends at or after this year).
 * - `'inactive'` — the year falls inside any inactive period that comes after
 *   the company has had at least one active year (interior gap or trailing
 *   dormancy).
 * - `'active'` — the year is not covered by any inactive period.
 *
 * `endDate` is exclusive (see Details tab: `2007-01-01` → `2021-01-01` covers
 * 2007–2020). A `null` `endDate` means the inactive period is still ongoing.
 */
export function classifyCompanyYear(
  year: number,
  inactivePeriods: CompanyInactivePeriodDto[],
  earliestSystemYear?: number
): CompanyYearClassification {
  if (!inactivePeriods || inactivePeriods.length === 0) {
    return { status: 'active', firstActiveYear: earliestSystemYear ?? null };
  }

  const isInactive = (y: number): boolean =>
    inactivePeriods.some(p => {
      const start = new Date(p.startDate).getUTCFullYear();
      const end = p.endDate ? new Date(p.endDate).getUTCFullYear() : Number.POSITIVE_INFINITY;
      return y >= start && y < end;
    });

  // First active year = first year (>= the system's earliest measurement) not in any
  // inactive period. Without an earliestSystemYear we'd anchor at the earliest inactive
  // period's start, which is wrong for interior gaps — a company whose only inactive
  // period is, e.g., 2017–2017 would incorrectly report firstActiveYear=2018 and tag
  // every year before that as 'before_start'.
  const minPeriodStart = Math.min(
    ...inactivePeriods.map(p => new Date(p.startDate).getUTCFullYear())
  );
  let candidate = earliestSystemYear ?? minPeriodStart;
  const SAFETY_CAP = 1000;
  for (let i = 0; i < SAFETY_CAP && isInactive(candidate); i++) {
    candidate++;
  }
  const firstActiveYear: number | null = isInactive(candidate) ? null : candidate;

  let status: CompanyYearStatus = 'active';
  if (firstActiveYear !== null && year < firstActiveYear) {
    status = 'before_start';
  } else if (isInactive(year)) {
    status = 'inactive';
  }

  return { status, firstActiveYear };
}

export function getInactivityMessage(classification: CompanyYearClassification): string | null {
  switch (classification.status) {
    case 'before_start':
      return classification.firstActiveYear !== null
        ? `The company was activated for the first time in measurement: ${classification.firstActiveYear}`
        : null;
    case 'inactive':
      return 'The company is inactive this year.';
    default:
      return null;
  }
}
